import {
    Box,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Image,
    Input,
    Spinner,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import imageCompression from 'browser-image-compression';
import { Button, Icon, IconButton, Modal } from '../../ui';
import InventoryImageCropDialog from './InventoryImageCropDialog';
import InventoryItemImage from './InventoryItemImage';

export interface InventoryItemImageFieldProps {
    /** Current display URL (existing signed URL or local object URL preview). */
    previewUrl: string | null;
    onFileSelected: (file: File) => void | Promise<void>;
    onClear: () => void;
    isUploading?: boolean;
    isDisabled?: boolean;
    label?: string;
    /** When true, render without the "Image" form label header. */
    hideLabel?: boolean;
    /** When true, the preview fills the full column width instead of capping at 200px. */
    fullWidth?: boolean;
    /** When true, the preview is a square that fills its container width. */
    squarePreview?: boolean;
    /** Crop dialog title — `${name} · ${manufacturerPartNumber}`. */
    cropTitle: string;
    /** Shown under the Yaxle mark when the photo is missing or fails to load. */
    alt?: string | null;
    /**
     * `tile` renders a small square thumbnail: "Add photo" when empty, and a
     * click-to-enlarge viewer (with Replace / Remove) once a photo exists.
     */
    variant?: 'default' | 'tile';
}

const InventoryItemImageField = ({
    previewUrl,
    onFileSelected,
    onClear,
    isUploading = false,
    isDisabled = false,
    label = 'Image',
    hideLabel = false,
    fullWidth = false,
    squarePreview = false,
    cropTitle,
    alt,
    variant = 'default',
}: InventoryItemImageFieldProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [compressError, setCompressError] = useState<string | null>(null);
    const [isCompressing, setIsCompressing] = useState(false);
    const [rawFile, setRawFile] = useState<File | null>(null);
    const [rawPreviewUrl, setRawPreviewUrl] = useState<string | null>(null);
    const [isViewerOpen, setIsViewerOpen] = useState(false);

    const closeCropDialog = () => {
        setRawPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
        setRawFile(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    const handlePick = (file: File | undefined) => {
        if (!file) return;
        setCompressError(null);
        setRawFile(file);
        setRawPreviewUrl(URL.createObjectURL(file));
    };

    const handleCropCancel = () => {
        closeCropDialog();
    };

    const handleCropConfirm = async (croppedFile: File) => {
        closeCropDialog();
        setCompressError(null);
        setIsCompressing(true);
        try {
            const compressed = await imageCompression(croppedFile, {
                maxSizeMB: 1.5,
                maxWidthOrHeight: 1600,
                useWebWorker: true,
            });
            const asFile = new File([compressed], croppedFile.name, {
                type: croppedFile.type || 'image/jpeg',
            });
            await onFileSelected(asFile);
        } catch {
            setCompressError('Failed to process image.');
        } finally {
            setIsCompressing(false);
        }
    };

    const busy = isUploading || isCompressing;
    /** The preview box and the action row share this cap so they stay flush. */
    const previewMaxW = squarePreview || fullWidth ? undefined : '200px';
    // With no photo, the placeholder itself is the upload control — no button row.
    const placeholderIsUpload = !previewUrl;
    const canUpload = !isDisabled && !busy;

    const fileInput = (
        <Input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            display="none"
            onChange={(e) => handlePick(e.target.files?.[0])}
        />
    );

    const cropDialog =
        rawFile && rawPreviewUrl ? (
            <InventoryImageCropDialog
                isOpen
                imageSrc={rawPreviewUrl}
                fileName={rawFile.name}
                fileType={rawFile.type}
                title={cropTitle}
                onCancel={handleCropCancel}
                onConfirm={handleCropConfirm}
            />
        ) : null;

    if (variant === 'tile') {
        const openPicker = () => {
            if (canUpload) inputRef.current?.click();
        };
        const handleTileActivate = () => {
            if (placeholderIsUpload) openPicker();
            else setIsViewerOpen(true);
        };
        const isInteractive = placeholderIsUpload ? canUpload : true;

        return (
            <Box>
                <Box
                    role="button"
                    tabIndex={isInteractive ? 0 : -1}
                    aria-label={placeholderIsUpload ? 'Add photo' : 'View photo'}
                    w="100%"
                    aspectRatio={1}
                    borderWidth="1px"
                    borderStyle={placeholderIsUpload ? 'dashed' : 'solid'}
                    borderColor="border.default"
                    borderRadius="lg"
                    bg="bg.surface"
                    overflow="hidden"
                    position="relative"
                    cursor={isInteractive ? 'pointer' : 'not-allowed'}
                    transition="border-color 0.15s ease, background 0.15s ease"
                    _hover={isInteractive ? { borderColor: 'brand.solid' } : undefined}
                    data-group
                    onClick={handleTileActivate}
                    onKeyDown={(event: React.KeyboardEvent) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleTileActivate();
                        }
                    }}
                >
                    {placeholderIsUpload ? (
                        <Flex
                            direction="column"
                            align="center"
                            justify="center"
                            gap={2}
                            h="100%"
                            color={canUpload ? 'brand.fg' : 'fg.muted'}
                        >
                            <Icon name="upload" size="lg" />
                            <Text fontSize="sm" fontWeight="semibold">
                                Add photo
                            </Text>
                        </Flex>
                    ) : (
                        <>
                            <InventoryItemImage src={previewUrl} alt={alt} />
                            {!busy && !isDisabled && (
                                <HStack
                                    position="absolute"
                                    bottom={1.5}
                                    right={1.5}
                                    spacing={1}
                                    opacity={0}
                                    transition="opacity 0.15s ease"
                                    _groupHover={{ opacity: 1 }}
                                    _groupFocusWithin={{ opacity: 1 }}
                                >
                                    <Tooltip label="Replace photo" placement="top">
                                        <IconButton
                                            aria-label="Replace photo"
                                            icon={<Icon name="upload" size="sm" />}
                                            size="xs"
                                            variant="outlineGray"
                                            bg="white"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openPicker();
                                            }}
                                        />
                                    </Tooltip>
                                    <Tooltip label="Remove photo" placement="top">
                                        <IconButton
                                            aria-label="Remove photo"
                                            icon={<Icon name="trash" size="sm" />}
                                            size="xs"
                                            variant="outline"
                                            colorScheme="red"
                                            bg="white"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onClear();
                                            }}
                                        />
                                    </Tooltip>
                                </HStack>
                            )}
                        </>
                    )}
                    {busy && (
                        <Box
                            position="absolute"
                            inset={0}
                            bg="blackAlpha.400"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Spinner color="white" size="md" />
                        </Box>
                    )}
                </Box>
                {compressError && (
                    <Text fontSize="xs" color="error.fg" mt={1}>
                        {compressError}
                    </Text>
                )}
                {fileInput}
                {cropDialog}
                <Modal
                    isOpen={isViewerOpen && !!previewUrl}
                    onClose={() => setIsViewerOpen(false)}
                    size="4xl"
                    title={alt?.trim() || 'Photo'}
                    footer={
                        <>
                            <Button
                                variant="outline"
                                colorScheme="red"
                                leftIcon={<Icon name="trash" size="sm" />}
                                isDisabled={isDisabled || busy}
                                onClick={() => {
                                    setIsViewerOpen(false);
                                    onClear();
                                }}
                            >
                                Remove
                            </Button>
                            <Button
                                variant="outlineGray"
                                leftIcon={<Icon name="upload" size="sm" />}
                                isDisabled={isDisabled || busy}
                                onClick={() => {
                                    setIsViewerOpen(false);
                                    openPicker();
                                }}
                            >
                                Replace
                            </Button>
                            <Button onClick={() => setIsViewerOpen(false)}>Done</Button>
                        </>
                    }
                >
                    <Flex
                        justify="center"
                        align="center"
                        bg="bg.surface"
                        borderRadius="md"
                        overflow="hidden"
                        minH="240px"
                    >
                        {previewUrl && (
                            <Image
                                src={previewUrl}
                                alt={alt?.trim() ?? ''}
                                maxW="100%"
                                maxH="70vh"
                                objectFit="contain"
                            />
                        )}
                    </Flex>
                </Modal>
            </Box>
        );
    }

    return (
        <FormControl>
            {!hideLabel && <FormLabel fontSize="sm">{label}</FormLabel>}
            <VStack align="stretch" spacing={2}>
                <Box
                    w="100%"
                    maxW={previewMaxW}
                    maxH={squarePreview ? undefined : fullWidth ? '320px' : '200px'}
                    minH={squarePreview ? undefined : '120px'}
                    aspectRatio={squarePreview ? 1 : undefined}
                    borderWidth="1px"
                    // Dashed dropzone while empty; a firm Smoke outline once a
                    // photo is in place.
                    borderStyle={placeholderIsUpload ? 'dashed' : 'solid'}
                    borderColor={placeholderIsUpload ? 'border.default' : 'border.strong'}
                    borderRadius="md"
                    bg="bg.surface"
                    overflow="hidden"
                    position="relative"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    {...(placeholderIsUpload
                        ? {
                              role: 'button',
                              tabIndex: canUpload ? 0 : -1,
                              'aria-label': 'Upload photo',
                              cursor: canUpload ? 'pointer' : 'not-allowed',
                              onClick: () => {
                                  if (canUpload) inputRef.current?.click();
                              },
                              onKeyDown: (event: React.KeyboardEvent) => {
                                  if (!canUpload) return;
                                  if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      inputRef.current?.click();
                                  }
                              },
                              _hover: canUpload
                                  ? { borderColor: 'brand.solid', bg: 'bg.subtle' }
                                  : undefined,
                              transition: 'border-color 0.15s ease, background 0.15s ease',
                          }
                        : {})}
                >
                    <Box
                        w="100%"
                        h={squarePreview ? '100%' : undefined}
                        minH={squarePreview ? undefined : '120px'}
                        maxH={squarePreview ? undefined : fullWidth ? '320px' : '200px'}
                    >
                        <InventoryItemImage
                            src={previewUrl}
                            alt={alt}
                            hint={
                                placeholderIsUpload && canUpload ? (
                                    <HStack spacing={1.5} color="fg.muted" mt={1}>
                                        <Icon name="upload" size="sm" />
                                        <Text fontSize="xs" fontWeight="medium">
                                            Click to upload
                                        </Text>
                                    </HStack>
                                ) : undefined
                            }
                        />
                    </Box>
                    {/* Actions ride on the photo so the field stays one row tall. */}
                    {previewUrl && (
                        <HStack
                            position="absolute"
                            bottom={0}
                            left={0}
                            right={0}
                            spacing={2}
                            px={2}
                            pb={2}
                        >
                            <Button
                                size="sm"
                                variant="outline"
                                flex={1}
                                bg="white"
                                _hover={{ bg: 'bg.surface' }}
                                leftIcon={<Icon name="upload" size="sm" />}
                                onClick={() => inputRef.current?.click()}
                                isDisabled={isDisabled || busy}
                            >
                                Replace
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                colorScheme="red"
                                flex={1}
                                bg="white"
                                _hover={{ bg: 'error.subtle' }}
                                leftIcon={<Icon name="trash" size="sm" />}
                                onClick={onClear}
                                isDisabled={isDisabled || busy}
                            >
                                Remove
                            </Button>
                        </HStack>
                    )}
                    {busy && (
                        <Box
                            position="absolute"
                            inset={0}
                            bg="blackAlpha.400"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Spinner color="white" size="md" />
                        </Box>
                    )}
                </Box>
                {compressError && (
                    <Text fontSize="sm" color="red.500">
                        {compressError}
                    </Text>
                )}
                {fileInput}
            </VStack>
            {cropDialog}
        </FormControl>
    );
};

export default InventoryItemImageField;
